import { IDeck } from "@models/deck";
import { getDeckFSRSRetention } from "@models/deckSettings";
import { IKnol } from "@models/knol";
import * as FSRSMod from "ts-fsrs";

/**
 * In-memory FSRS scheduler for scheduling and updating flashcards.
 * Cards must have at least an id and the minimal FSRS scheduling fields.
 */

export interface IFSRSCard {
  stability: number;
  difficulty: number;
  due: number; // Unix timestamp in ms
  state: FSRSMod.StateType;
  lastReview: number; // Unix timestamp in ms
}
export interface FSRSCard extends IFSRSCard {
  id: string;
  // Optionally, bring additional fields from your app as needed.
}

/** Default FSRS model parameters; you might want to load/override these */
const defaultFsrs = new FSRSMod.FSRS({});

/**
 * Get FSRS instance with deck-specific parameters
 */
export function getFSRSForDeck(deck?: IDeck): FSRSMod.FSRS {
  if (!deck) {
    return defaultFsrs;
  }

  const requestRetention = getDeckFSRSRetention(deck);
  const params = FSRSMod.generatorParameters({
    request_retention: requestRetention,
  });

  return new FSRSMod.FSRS(params);
}

/** Legacy export for backward compatibility */
export { defaultFsrs as fsrs };

/**
 * Safely extract an FSRSCard from an IKnol, or assign sensible defaults
 * - Returns undefined if not enough info is present (no id)
 * - For legacy cards (without FSRS fields), uses basic defaults with "New"
 */
export function knolToFSRSCard(
  knol: IKnol | undefined,
  now: number = Date.now(),
): FSRSCard | undefined {
  if (!knol || !knol.id) return undefined;

  // Detect FSRS scheduling fields living directly on knol
  // Extend this logic if your system stores them in another way
  // @ts-ignore
  const stability = typeof knol.stability === "number" ? knol.stability : 0;
  // @ts-ignore
  const difficulty = typeof knol.difficulty === "number" ? knol.difficulty : 0;
  // @ts-ignore
  const due = typeof knol.due === "number" ? knol.due : now;
  // @ts-ignore: state may be stored on knol metadata but not declared on IKnol
  const state = (typeof knol.state === "string" ? knol.state : "New") as FSRSMod.StateType;
  // @ts-ignore
  const lastReview = typeof knol.lastReview === "number" ? knol.lastReview : now;

  return {
    id: knol.id,
    stability,
    difficulty,
    due,
    state,
    lastReview,
  };
}

/** Initialize an FSRS card for a new item */
export function createFSRSCard(id: string, now: number = Date.now()): FSRSCard {
  // This mirrors https://github.com/open-spaced-repetition/fsrs-core/blob/main/rs/src/card.rs#L24
  // Values are FSRS-defaults for a new item.
  return {
    id,
    stability: 0,
    difficulty: 0,
    due: now,
    state: "New" as FSRSMod.StateType,
    lastReview: now,
  };
}

/** Find and return cards that are currently due (i.e., need review) */
export function getDueCards(cards: FSRSCard[], now: number = Date.now()): FSRSCard[] {
  return cards.filter((card) => card.due <= now && card.state !== "New");
}

/**
 * Get the next N due cards, sorted by earliest due first.
 * If fewer than N due, returns all due.
 */
export function getNextDueCards(
  cards: FSRSCard[],
  limit: number,
  now: number = Date.now(),
): FSRSCard[] {
  const due = getDueCards(cards, now);
  due.sort((a, b) => a.due - b.due);
  return due.slice(0, limit);
}

/** Map your UI/response input to FSRS's Rating */
export function toFSRSRating(raw: "again" | "hard" | "good" | "easy"): FSRSMod.GradeType {
  switch (raw) {
    case "again":
      return "Again";
    case "hard":
      return "Hard";
    case "good":
      return "Good";
    case "easy":
      return "Easy";
    default:
      return "Again";
  }
}

/**
 * Update the FSRS fields on a card using the user's response (rating).
 * Modifies the provided card object in-place and returns it.
 */
export function updateFSRSCard(
  card: FSRSCard,
  rating: FSRSMod.GradeType,
  now: number = Date.now(),
  deck?: IDeck,
): FSRSCard {
  const fsrsInstance = getFSRSForDeck(deck);
  const fsrsCard = FSRSMod.TypeConvert.card({
    due: card.due,
    stability: card.stability,
    difficulty: card.difficulty,
    state: card.state,
    last_review: card.lastReview,
  } as any);
  const grade = FSRSMod.Rating[rating];
  const recordLogItem = fsrsInstance.next(fsrsCard, new Date(now), grade);
  const nextCard = recordLogItem.card;

  card.stability = nextCard.stability;
  card.difficulty = nextCard.difficulty;
  card.due = nextCard.due.getTime();
  card.state = FSRSMod.State[nextCard.state] as FSRSMod.StateType;
  card.lastReview = now;
  return card;
}

/**
 * Map card FSRS state/stability/difficulty to a grade string (legacy display support).
 * This is heuristic/example; adjust as you wish.
 */
/**
 * Accepts FSRSCard or IKnol (runtime-checked), or undefined if no grade possible.
 */
export function fsrsGrade(cardOrKnol: FSRSCard | IKnol | undefined): string {
  let card: FSRSCard | undefined;
  if (cardOrKnol && (cardOrKnol as any).stability === undefined) {
    // IKnol or incomplete object - try convert
    card = knolToFSRSCard(cardOrKnol as IKnol);
  } else {
    // Already FSRSCard
    card = cardOrKnol as FSRSCard;
  }
  if (!card) return "NEW";
  if (card.state === "New") return "NEW";
  if (card.difficulty < 2.7) return "AAA";
  if (card.difficulty < 3.6) return "AA";
  if (card.difficulty < 4.0) return "A";
  if (card.stability > 365) return "A";
  if (card.stability > 120) return "B";
  if (card.stability > 60) return "C";
  if (card.stability > 20) return "D";
  if (card.stability > 5) return "E";
  return "F";
}

/** Utility: sort cards by due date, next due soonest first */
export function sortByDue(cards: FSRSCard[]): FSRSCard[] {
  return cards.slice().sort((a, b) => a.due - b.due);
}

/**
 * Compute counts of cards due today (<= now), tomorrow (> now & <= now+1d),
 * and upcoming within the next 7 days (> now+1d & <= now+7d).
 */
export function dueCounts(
  cards: FSRSCard[],
  now: number = Date.now(),
): { today: number; tomorrow: number; next7d: number } {
  const todayCutoff = now;
  const tomorrowCutoff = now + 1000 * 60 * 60 * 24;
  const weekCutoff = now + 1000 * 60 * 60 * 24 * 7;
  let today = 0;
  let tomorrow = 0;
  let next7d = 0;
  for (const card of cards) {
    // Exclude new cards from due counts
    if (card.state === "New") {
      continue;
    }
    if (card.due <= todayCutoff) {
      today++;
    } else if (card.due <= tomorrowCutoff) {
      tomorrow++;
    } else if (card.due <= weekCutoff) {
      next7d++;
    }
  }
  return { today, tomorrow, next7d };
}

/**
 * Return the next N cards sorted by due date, regardless of whether they are due yet.
 * Useful for "get ahead" reviews of future-due cards.
 */
export function getUpcomingCards(cards: FSRSCard[], limit: number): FSRSCard[] {
  return sortByDue(cards).slice(0, limit);
}

/** Bulk-initialize a set of FSRS cards from IDs */
export function initializeFSRSDeck(ids: string[], now: number = Date.now()): FSRSCard[] {
  return ids.map((id) => createFSRSCard(id, now));
}
